import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkLocationAvailability } from '../utils/locationUtils';

/**
 * Hook that checks location permissions when the app regains focus.
 * If location is enabled in preferences but permission is not granted,
 * it will prompt the user to grant permission.
 * 
 * @param {boolean} locationEnabled - Whether location-aware tasks are enabled in user preferences
 * @param {boolean} enabled - Whether the check should be enabled (default: true)
 */
export function useLocationPermissionCheck(locationEnabled, enabled = true) {
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkPermission = useCallback(async () => {
    if (!enabled || !locationEnabled) {
      setShowPermissionPrompt(false);
      return;
    }

    try {
      const availability = await checkLocationAvailability();
      setPermissionStatus(availability.permission);

      // If location is enabled but permission is not granted, show prompt
      // Don't show if we're already on the account page (user might be in preferences)
      if (availability.permission !== 'granted' && location.pathname !== '/account') {
        setShowPermissionPrompt(true);
      } else {
        setShowPermissionPrompt(false);
      }
    } catch (err) {
      console.error('Error checking location permission:', err);
    }
  }, [enabled, locationEnabled, location.pathname]);

  useEffect(() => {
    if (!enabled || !locationEnabled) {
      setShowPermissionPrompt(false);
      return;
    }

    // Check on mount
    checkPermission();

    // Check when app regains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Use queueMicrotask for non-blocking async check
        queueMicrotask(() => {
          checkPermission();
        });
      }
    };

    // Also listen for permission changes
    let permissionStatus = null;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        permissionStatus = status;
        status.onchange = () => {
          checkPermission();
        };
      }).catch(() => {
        // Permissions API not fully supported, ignore
      });
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [enabled, locationEnabled, checkPermission]);

  const handleGoToPreferences = () => {
    navigate('/account/preferences');
  };

  const handleDismiss = () => {
    setShowPermissionPrompt(false);
  };

  return {
    showPermissionPrompt,
    permissionStatus,
    handleGoToPreferences,
    handleDismiss
  };
}

